/* Spray Skirt Size Selector — per-instance, clone-safe.

   Replaces the getElementById-based assets/custom-script.js. That script cannot
   be embedded anywhere the markup might appear twice, and the product-page size
   modal CLONES the page body at runtime, so "twice" is the normal case rather
   than the exotic one. Four things it did that this deliberately does not:

   1. `document.getElementById` for the form, all five selects, the button and
      the modal. Duplicate ids resolve to the FIRST match in document order --
      the copy sitting behind the modal -- so every control drove the hidden
      instance while looking correct. Same failure as the first cm/inches
      toggle, 2026-07-29. Here every lookup is root.querySelector, and the root
      comes from closest('.ls-skirt-selector').
   2. A fresh `addEventListener('change')` on every repopulate, so listeners
      accumulated for as long as the page was open. Here there are exactly three
      listeners, delegated on `document`, for the whole page however many copies
      exist. Delegation is also what makes a cloned copy work at all: a listener
      bound to a node does not survive being cloned.
   3. `setInterval(..., 10000)` running forever, dereferencing `.no-product`
      with no null check, so it threw every ten seconds on any page that loaded
      the script without the markup. There is no timer here.
   4. Fetching the whole 5.4 MB data file on page load AND again on every button
      click. Here it is fetched once per URL, lazily, and shared by every
      instance through a module-scoped promise cache.

   The data file is now per-store and generated by
   _scripts/shopify/build_skirt_selector_data.py. Its `tree` is nested in
   dropdown order, so a cascade is a walk rather than five passes of filter()
   over 21,109 rows. A leaf of null means "no Level Six skirt fits this
   combination", which is an ANSWER -- the old data expressed it as the string
   "Not Found", which the old script treated as a URL and opened.

   ES5 on purpose: same bar as assets/ls-size-chart.js, which has to run in the
   size modal on whatever the customer is holding. */
(function () {
  var ROOT = '.ls-skirt-selector';
  var LEVELS = ['.ls-ss-mfr', '.ls-ss-model', '.ls-ss-material', '.ls-ss-skirt', '.ls-ss-size'];
  var READY = 'data-ls-ready';

  /* url -> Promise. Shared across instances so eleven charts plus a product
     page do not fetch eleven copies. */
  var cache = {};

  /* WHY NONE OF THE PER-INSTANCE STATE IS AN ATTRIBUTE.
     cloneNode copies attributes. If "I already have my data" lived in a
     data- attribute, the modal's clone would inherit `ready` while holding no
     reference to any data, and would then skip preparation forever: dead
     dropdowns that look initialised. Attributes are the one place this state
     must not go. A WeakMap key is the element itself, so a clone is correctly
     an unknown element; the ES5 fallback uses an expando property, which
     cloneNode also does not copy. `data-ls-ready` is still written, but only
     as a CSS/debug mirror -- nothing branches on reading it. */
  var loaded = (typeof WeakMap === 'function') ? new WeakMap() : null;
  var busy = (typeof WeakMap === 'function') ? new WeakMap() : null;
  var DATA_KEY = '__lsSkirtData';
  var BUSY_KEY = '__lsSkirtBusy';

  function setData(root, data) {
    if (loaded) { loaded.set(root, data); } else { root[DATA_KEY] = data; }
  }
  function getData(root) {
    return loaded ? loaded.get(root) : root[DATA_KEY];
  }
  function setBusy(root, v) {
    if (busy) { busy.set(root, v); } else { root[BUSY_KEY] = v; }
  }
  function isBusy(root) {
    return !!(busy ? busy.get(root) : root[BUSY_KEY]);
  }
  function isPrepared(root) {
    return !!getData(root) || isBusy(root);
  }

  function selectAt(root, i) { return root.querySelector(LEVELS[i]); }

  function msg(root, text) {
    var el = root.querySelector('.ls-ss-msg');
    if (!el) return;
    if (text) { el.textContent = text; el.removeAttribute('hidden'); }
    else { el.textContent = ''; el.setAttribute('hidden', ''); }
  }

  /* Walk `values` down the tree. Returns undefined for a path that is not in
     the data, the child object for a partial path, and the leaf (a product
     index, or null for "nothing fits") for a complete one. */
  function nodeAt(data, values) {
    var node = data && data.tree, i;
    for (i = 0; i < values.length; i++) {
      if (!node || typeof node !== 'object') return undefined;
      node = node[values[i]];
    }
    return node;
  }

  function valuesUpTo(root, depth) {
    var out = [], i, sel;
    for (i = 0; i < depth; i++) {
      sel = selectAt(root, i);
      if (!sel || !sel.value) return out;
      out.push(sel.value);
    }
    return out;
  }

  /* Options come out in the data file's own order rather than sorted. Size is
     the reason: S, M, L, XL, XXL is meaningful and alphabetical is not, and a
     rule that sorts four levels but not the fifth is a rule someone will get
     wrong later. The source order is the merchandiser's order. */
  function fill(sel, keys) {
    var placeholder = sel.options.length ? sel.options[0] : null;
    var text = placeholder ? placeholder.textContent : '';
    var i, opt;
    sel.innerHTML = '';
    opt = document.createElement('option');
    opt.value = '';
    opt.textContent = text;
    sel.appendChild(opt);
    for (i = 0; i < keys.length; i++) {
      opt = document.createElement('option');
      opt.value = keys[i];
      opt.textContent = keys[i];
      sel.appendChild(opt);
    }
  }

  function resetFrom(root, depth) {
    for (var i = depth; i < LEVELS.length; i++) {
      var sel = selectAt(root, i);
      if (!sel) continue;
      fill(sel, []);
      sel.value = '';
      sel.disabled = true;
    }
  }

  /* Repopulate level `depth` from the current selections above it. */
  function cascade(root, depth) {
    var data = getData(root);
    if (!data) return;
    resetFrom(root, depth);
    var sel = selectAt(root, depth);
    if (!sel) return;
    var node = nodeAt(data, valuesUpTo(root, depth));
    if (!node || typeof node !== 'object') return;
    var keys = [];
    for (var k in node) { if (Object.prototype.hasOwnProperty.call(node, k)) keys.push(k); }
    fill(sel, keys);
    sel.disabled = keys.length === 0;
  }

  function load(root) {
    var url = root.getAttribute('data-src');
    if (!url) return null;
    if (!cache[url]) {
      cache[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
    }
    return cache[url];
  }

  /* Prepare an instance: fetch once, then populate the first dropdown and reset
     the rest. Always resets, which is what makes a cloned copy behave as its
     own control rather than inheriting whatever the original had selected. */
  function prepare(root) {
    if (isPrepared(root)) return;
    setBusy(root, true);
    root.setAttribute(READY, 'loading');
    var p = load(root);
    if (!p) { setBusy(root, false); root.setAttribute(READY, 'no'); return; }
    p.then(function (data) {
      setData(root, data);
      setBusy(root, false);
      root.setAttribute(READY, 'yes');
      cascade(root, 0);
      msg(root, '');
    }).catch(function () {
      setBusy(root, false);
      root.setAttribute(READY, 'no');
      msg(root, 'The boat list could not be loaded. Please refresh, or contact us and we will size it for you.');
    });
  }

  /* Deferred until the instance is on screen. On the standalone selector page
     that is immediately; inside a closed size modal it is when the modal opens,
     which is the whole point of not fetching on page load. */
  var io = null;
  function observe(root) {
    if (!('IntersectionObserver' in window)) { prepare(root); return; }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.unobserve(entries[i].target);
            prepare(entries[i].target);
          }
        }
      }, { rootMargin: '200px' });
    }
    io.observe(root);
  }

  function init() {
    var roots = document.querySelectorAll(ROOT), i, root;
    for (i = 0; i < roots.length; i++) {
      root = roots[i];
      if (isPrepared(root)) continue;
      observe(root);
    }
  }

  /* --- three delegated listeners, for every instance on the page ---------- */

  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || !sel.className || String(sel.className).indexOf('ls-ss-select') === -1) return;
    var root = sel.closest ? sel.closest(ROOT) : null;
    if (!root) return;
    msg(root, '');
    if (!getData(root)) { prepare(root); return; }
    var depth = parseInt(sel.getAttribute('data-ls-level'), 10);
    if (isNaN(depth)) return;
    if (!sel.value) { resetFrom(root, depth + 1); return; }
    cascade(root, depth + 1);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.ls-ss-go') : null;
    if (!btn) return;
    var root = btn.closest(ROOT);
    if (!root) return;
    e.preventDefault();

    var data = getData(root);
    if (!data) { prepare(root); msg(root, 'One moment, still loading the boat list.'); return; }

    var values = valuesUpTo(root, LEVELS.length);
    if (values.length < LEVELS.length) {
      msg(root, 'Choose all five before searching.');
      var next = selectAt(root, values.length);
      if (next && next.focus) next.focus();
      return;
    }

    var leaf = nodeAt(data, values);
    if (typeof leaf === 'number' && data.products && data.products[leaf]) {
      msg(root, 'Opening ' + values[3] + ', size ' + values[4] + ' in a new tab.');
      window.open(data.products[leaf].url, '_blank', 'noopener');
      return;
    }
    /* null leaf: a real answer, not a failure. The old data said "Not Found"
       and the old script opened it as a URL. */
    msg(root, 'No Level Six skirt fits that combination. Contact us and we will help you find one.');
  }, true);

  /* Belt and braces for a copy the modal clones in after init has run and that
     an IntersectionObserver never saw. Guarded on isPrepared, never on the
     attribute, because the clone arrives carrying the original's attributes. */
  document.addEventListener('focusin', function (e) {
    var root = e.target && e.target.closest ? e.target.closest(ROOT) : null;
    if (root && !isPrepared(root)) prepare(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
  /* Same 1200ms re-init as assets/ls-size-chart.js, for markup that arrives
     after DOMContentLoaded. */
  setTimeout(init, 1200);
})();
